import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { networkErrorHandler, getErrorMessage, getFallbackData } from '@/utils/networkErrorHandler';

interface ZoneFilters {
  activeTab?: 'climate' | 'hardiness';
  climateZone?: string;
  country?: string;
  hardinessZone?: string;
  showMyZoneOnly?: boolean;
}

interface PaginationParams {
  limit?: number;
  cursor?: string; // ISO timestamp for cursor pagination
  direction?: 'before' | 'after';
}

interface OptimizedPost {
  id: string;
  user_id: string;
  author_id: string;
  org_id?: string;
  content: string;
  // Dual field names for compatibility
  image_url: string | null;
  image: string | null; // Alias for PostCard
  visibility: 'public' | 'followers' | 'org' | 'private';
  is_video: boolean;
  isVideo: boolean; // Alias for PostCard
  likes_count: number;
  likes: number; // Alias for PostCard
  comments_count: number;
  comments: number; // Alias for PostCard
  created_at: string;
  crop_type: string | null;
  crop: string | null; // Alias for PostCard
  sentiment: string | null;
  whats_happening: string | null;
  whatsHappening: string | null; // Alias for PostCard
  challenges: string | null;
  post_type: string;
  // Joined profile data
  user: {
    name: string;
    username: string;
    avatar: string;
    verified: boolean;
  };
  // Computed fields
  isLiked?: boolean;
  timestamp: string;
}

export const useOptimizedPosts = (
  zoneFilters?: ZoneFilters, 
  isLiteMode: boolean = false,
  enabled: boolean = true
) => {
  const [posts, setPosts] = useState<OptimizedPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);

  const defaultLimit = isLiteMode ? 5 : 10;

  // Only select columns that are actually rendered in PostCard
  const selectColumns = `
    id,
    user_id,
    author_id,
    org_id,
    content,
    image_url,
    visibility,
    is_video,
    likes_count,
    comments_count,
    created_at,
    crop_type,
    sentiment,
    whats_happening,
    challenges,
    post_type
  `;

  const formatTimestamp = (timestamp: string): string => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));
    
    if (diffInHours < 1) return 'Just now';
    if (diffInHours < 24) return `${diffInHours} hours ago`;
    if (diffInHours < 48) return '1 day ago';
    return `${Math.floor(diffInHours / 24)} days ago`;
  };

  const fetchPosts = useCallback(async (
    pagination?: PaginationParams,
    filters?: ZoneFilters,
    skipLoadingState = false
  ) => {
    const currentFilters = filters || zoneFilters;
    const limit = pagination?.limit || defaultLimit;
    
    try {
      if (!skipLoadingState) {
        setLoading(true);
        setError(null);
      }
      
      const result = await networkErrorHandler.withRetry(async () => {
        const { data: { user } } = await supabase.auth.getUser();
        
        // Build base query
        let query = supabase
          .from('posts')
          .select(selectColumns)
          .order('created_at', { ascending: false })
          .limit(limit);

        // Add cursor pagination
        if (pagination?.cursor) {
          if (pagination.direction === 'after') {
            query = query.lt('created_at', pagination.cursor);
          } else {
            query = query.gt('created_at', pagination.cursor);
          }
        }

        // Apply zone filtering if enabled
        if (currentFilters?.showMyZoneOnly && user) {
          const { data: userProfile } = await supabase
            .from('profiles')
            .select('climate_zone, hardiness_zone')
            .eq('user_id', user.id)
            .maybeSingle();

          if (userProfile) {
            let userIds: string[] = [];
            
            if (currentFilters.activeTab === 'climate' && userProfile.climate_zone) {
              const { data: usersInSameZone } = await supabase
                .from('profiles')
                .select('user_id')
                .eq('climate_zone', userProfile.climate_zone);
              userIds = usersInSameZone?.map(p => p.user_id) || [];
            } else if (currentFilters.activeTab === 'hardiness' && userProfile.hardiness_zone) {
              const { data: usersInSameZone } = await supabase
                .from('profiles')
                .select('user_id')
                .eq('hardiness_zone', userProfile.hardiness_zone);
              userIds = usersInSameZone?.map(p => p.user_id) || [];
            }
            
            if (userIds.length > 0) {
              query = query.in('user_id', userIds);
            }
          }
        }

        const { data: postsData, error: postsError } = await query;

        if (postsError) throw postsError;
        return { postsData, user };
      }, {
        maxRetries: 2,
        delayMs: 1000
      });

      const { postsData, user } = result;

      if (!postsData || postsData.length === 0) {
        if (!pagination?.cursor) {
          setPosts([]);
        }
        setHasMore(false);
        setRetryCount(0);
        return;
      }

      // Fetch profile data for all users in current batch
      const userIds = [...new Set(postsData.map(post => post.user_id))];
      const { data: profilesData } = await supabase
        .from('profiles')
        .select('user_id, username, farm_name, avatar_url')
        .in('user_id', userIds)
        .eq('is_active', true)
        .eq('is_discoverable', true);

      // Create profile lookup
      const profileLookup: { [key: string]: any } = {};
      if (profilesData) {
        profilesData.forEach(profile => {
          profileLookup[profile.user_id] = profile;
        });
      }

      // Get user likes for current batch
      let userLikes: string[] = [];
      if (user) {
        const { data: likesData } = await supabase
          .from('post_likes')
          .select('post_id')
          .eq('user_id', user.id)
          .in('post_id', postsData.map(p => p.id));
        
        userLikes = likesData?.map(like => like.post_id) || [];
      }

      // Transform posts with minimal data
      const transformedPosts: OptimizedPost[] = postsData.map((post: any) => {
        const profile = profileLookup[post.user_id];
        
        return {
          id: post.id,
          user_id: post.user_id,
          author_id: post.author_id,
          org_id: post.org_id,
          content: post.content,
          image: post.image_url, // Transform to match PostCard expectations
          visibility: post.visibility || 'public',
          isVideo: post.is_video, // Transform to match PostCard expectations 
          likes: post.likes_count, // Transform to match PostCard expectations
          comments: post.comments_count, // Transform to match PostCard expectations
          crop: post.crop_type, // Transform to match PostCard expectations
          sentiment: post.sentiment,
          whatsHappening: post.whats_happening, // Transform to match PostCard expectations
          challenges: post.challenges,
          created_at: post.created_at,
          post_type: post.post_type,
          user: {
            name: profile?.farm_name || 'Unknown Farm',
            username: profile?.username || 'unknown',
            avatar: profile?.avatar_url || "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=face",
            verified: Math.random() > 0.5, // TODO: Add verified field to profiles
          },
          isLiked: userLikes.includes(post.id),
          timestamp: formatTimestamp(post.created_at),
          // Keep original field names for compatibility
          image_url: post.image_url,
          is_video: post.is_video,
          likes_count: post.likes_count,
          comments_count: post.comments_count,
          crop_type: post.crop_type,
          whats_happening: post.whats_happening,
        };
      });

      // Update state based on pagination
      if (pagination?.cursor && pagination.direction === 'after') {
        // Load more (append to existing)
        setPosts(prev => [...prev, ...transformedPosts]);
      } else {
        // Initial load or refresh
        setPosts(transformedPosts);
      }

      // Set pagination state
      setHasMore(transformedPosts.length === limit);
      setNextCursor(transformedPosts.length > 0 ? transformedPosts[transformedPosts.length - 1].created_at : null);
      setRetryCount(0); // Reset retry count on success

    } catch (err: any) {
      console.error('Error fetching posts:', err);
      
      const userMessage = getErrorMessage(err);
      setError(userMessage);
      
      // Use fallback data if offline
      if (networkErrorHandler.isOffline() && !pagination?.cursor) {
        const fallbackData = getFallbackData('posts');
        setPosts(fallbackData);
      } else if (!pagination?.cursor) {
        setPosts([]);
      }
      
      setRetryCount(prev => prev + 1);
    } finally {
      setLoading(false);
    }
  }, [zoneFilters, defaultLimit]);

  const retry = () => {
    fetchPosts(undefined, undefined, true);
  };

  const loadMore = useCallback(() => {
    if (!loading && hasMore && nextCursor) {
      fetchPosts({
        limit: defaultLimit,
        cursor: nextCursor,
        direction: 'after'
      });
    }
  }, [loading, hasMore, nextCursor, fetchPosts, defaultLimit]);

  const refresh = useCallback(() => {
    setNextCursor(null);
    setHasMore(true);
    fetchPosts();
  }, [fetchPosts]);

  const toggleLike = async (postId: string) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        // Optimistic update for anonymous users
        setPosts(prevPosts => 
          prevPosts.map(post => 
            post.id === postId 
              ? { 
                  ...post, 
                  isLiked: !post.isLiked,
                  likes_count: post.isLiked ? post.likes_count - 1 : post.likes_count + 1
                }
              : post
          )
        );
        return;
      }

      // Check if user already liked this post
      const { data: existingLike } = await supabase
        .from('post_likes')
        .select('id')
        .eq('post_id', postId)
        .eq('user_id', user.id)
        .maybeSingle();

      if (existingLike) {
        // Remove like
        await supabase
          .from('post_likes')
          .delete()
          .eq('post_id', postId)
          .eq('user_id', user.id);
        
        // Update post likes count
        const currentPost = posts.find(p => p.id === postId);
        const newCount = Math.max(0, (currentPost?.likes_count || 0) - 1);
        await supabase
          .from('posts')
          .update({ likes_count: newCount })
          .eq('id', postId);
      } else {
        // Add like
        await supabase
          .from('post_likes')
          .insert({ post_id: postId, user_id: user.id });
        
        // Update post likes count
        const currentPost = posts.find(p => p.id === postId);
        const newCount = (currentPost?.likes_count || 0) + 1;
        await supabase
          .from('posts')
          .update({ likes_count: newCount })
          .eq('id', postId);
      }

      // Refresh posts to get updated counts
      refresh();
    } catch (err) {
      console.error('Error toggling like:', err);
    }
  };

  useEffect(() => {
    if (enabled) {
      fetchPosts();
    } else {
      // Clear posts if fetching is disabled (Lite mode, different tab)
      setPosts([]);
      setLoading(false);
      setHasMore(false);
    }
  }, [fetchPosts, enabled]);

  return {
    posts,
    loading,
    error,
    hasMore,
    loadMore,
    refresh,
    toggleLike,
    isLiteMode,
    retryCount,
    retry,
    isOffline: networkErrorHandler.isOffline()
  };
};